import { useState, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts'
import { cx } from '@/lib/utils'
import { ChartTooltipContent } from '@/components/application/charts/charts-base'
import type { DailyStatsData } from '@/types/dashboard'

export interface ConversationStatsChartProps {
  data: DailyStatsData[] | undefined
  className?: string
}

type ChartType = 'duration' | 'count'

// Get CSS variable value from the document
function getCSSVariable(name: string): string {
  if (typeof window === 'undefined') return ''
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

// Chart colors - read from CSS variables with fallbacks
function getChartColors() {
  const brandColor = getCSSVariable('--color-brand-500') || '#368f8b'
  const warningColor = getCSSVariable('--color-warning-500') || '#f79009'
  const brandLight = getCSSVariable('--color-brand-300') || '#63bab8'
  const warningLight = getCSSVariable('--color-warning-300') || '#fec84b'

  return {
    primary: brandColor,
    secondary: warningColor,
    gradient1Start: brandColor,
    gradient1End: brandLight,
    gradient2Start: warningColor,
    gradient2End: warningLight,
  }
}

// Custom legend component that uses actual chart colors
interface CustomLegendProps {
  payload?: Array<{
    value: string
    color?: string
    dataKey?: string
  }>
}

function CustomLegend({ payload }: CustomLegendProps) {
  if (!payload) return null

  return (
    <ul className="flex flex-row items-center justify-center gap-6">
      {payload.map((entry, index) => (
        <li key={index} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <span
            className="size-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          {entry.value}
        </li>
      ))}
    </ul>
  )
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}`
  }
  return `${minutes}m`
}

function formatTooltipDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes} min`
}

export function ConversationStatsChart({
  data,
  className,
}: ConversationStatsChartProps) {
  const { t } = useTranslation()
  const [chartType, setChartType] = useState<ChartType>('duration')
  const chartId = useId()

  // Get chart colors from CSS variables
  const CHART_COLORS = getChartColors()

  const chartData = data?.map((item) => ({
    date: format(parseISO(item.date), 'dd MMM'),
    fullDate: item.date,
    callDuration: item.call_duration,
    transferDuration: item.transfer_duration,
    directCalls: item.call_count - item.transferred_count,
    transferredCalls: item.transferred_count,
  }))

  const hasData = chartData && chartData.length > 0

  return (
    <div
      className={cx(
        'rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100 dark:bg-slate-800 dark:ring-slate-700',
        className
      )}
    >
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('resources.dashboard.chart.title')}
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {t('resources.dashboard.chart.subtitle')}
          </p>
        </div>

        {/* Toggle buttons */}
        <div className="inline-flex rounded-xl bg-slate-100 p-1 dark:bg-slate-700">
          <button
            onClick={() => setChartType('duration')}
            className={cx(
              'rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200',
              chartType === 'duration'
                ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-600 dark:text-white'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
            )}
          >
            {t('resources.dashboard.chart.duration')}
          </button>
          <button
            onClick={() => setChartType('count')}
            className={cx(
              'rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200',
              chartType === 'count'
                ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-600 dark:text-white'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
            )}
          >
            {t('resources.dashboard.chart.count')}
          </button>
        </div>
      </div>

      {/* Chart */}
      {!hasData ? (
        <div className="flex h-64 items-center justify-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('resources.dashboard.chart.no_data')}
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={288}>
            <ComposedChart
              data={chartData}
              margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
            >
              <defs>
                <linearGradient id={`${chartId}-gradient1`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.gradient1Start} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={CHART_COLORS.gradient1End} stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id={`${chartId}-gradient2`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.gradient2Start} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={CHART_COLORS.gradient2End} stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#e2e8f0"
                strokeOpacity={0.5}
              />

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickMargin={12}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickFormatter={(value) =>
                  chartType === 'duration' ? formatDuration(value) : value.toString()
                }
                width={60}
              />

              <Tooltip
                content={({ active, payload, label }) => (
                  <ChartTooltipContent
                    active={active}
                    payload={payload?.map((p) => ({
                      ...p,
                      value:
                        chartType === 'duration'
                          ? formatTooltipDuration(p.value as number)
                          : p.value,
                    }))}
                    label={label}
                    formatter={(value) => value}
                  />
                )}
              />

              <Legend content={<CustomLegend />} />

              {chartType === 'duration' ? (
                <>
                  <Area
                    type="monotone"
                    dataKey="callDuration"
                    name={t('resources.dashboard.chart.call_duration')}
                    stroke={CHART_COLORS.primary}
                    strokeWidth={2.5}
                    fill={`url(#${chartId}-gradient1)`}
                    dot={false}
                    activeDot={{
                      r: 6,
                      stroke: CHART_COLORS.primary,
                      strokeWidth: 2,
                      fill: 'white',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="transferDuration"
                    name={t('resources.dashboard.chart.transfer_duration')}
                    stroke={CHART_COLORS.secondary}
                    strokeWidth={2.5}
                    fill={`url(#${chartId}-gradient2)`}
                    dot={false}
                    activeDot={{
                      r: 6,
                      stroke: CHART_COLORS.secondary,
                      strokeWidth: 2,
                      fill: 'white',
                    }}
                  />
                </>
              ) : (
                <>
                  <Bar
                    dataKey="directCalls"
                    name={t('resources.dashboard.chart.direct_calls')}
                    stackId="calls"
                    fill={CHART_COLORS.primary}
                    radius={[0, 0, 4, 4]}
                    maxBarSize={40}
                  />
                  <Bar
                    dataKey="transferredCalls"
                    name={t('resources.dashboard.chart.transferred_calls')}
                    stackId="calls"
                    fill={CHART_COLORS.secondary}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
      )}
    </div>
  )
}

export default ConversationStatsChart
